import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import FilterDate from '../../view/FilterDate';
import FilterString from '../../view/FilterString';
import { DocumentNode } from 'graphql';
import CustomerNoteMutation from '../../../graphql/mutations/customer/CustomerNoteMutation.graphql';
import SupplierNoteMutation from '../../../graphql/mutations/supplier/SupplierNoteMutation.graphql';
import SalesNoteMutation from '../../../graphql/mutations/sales/SalesNoteMutation.graphql';
import SalesCreditNoteMutation from '../../../graphql/mutations/salesLedger/SalesCreditNoteMutation.graphql';
import PurchaseNoteMutation from '../../../graphql/mutations/purchases/PurchaseNoteMutation.graphql';
import SupplierReturnNoteMutation from '../../../graphql/mutations/purchases/SupplierReturnNoteMutation.graphql';
import NominalNoteMutation from '../../../graphql/mutations/nominal/NominalNoteMutation.graphql';
import DiaryNoteMutation from '../../../graphql/mutations/diary/DiaryNoteMutation.graphql';
import ProductNoteMutation from '../../../graphql/mutations/product/ProductNoteMutation.graphql';
import AssemblyNoteMutation from '../../../graphql/mutations/product/AssemblyNoteMutation.graphql';
import { ApolloQueryResult, useQuery } from '@apollo/client';
import {
  Direction,
  FilterOperation,
  IAssemblyNote,
  ICustomerNote,
  ICustomerNotesQueryVariables,
  IDiaryNote,
  IFilterInput,
  INominalNote,
  IProductNote,
  IPurchaseOrderNote,
  ISalesOrderNote,
  ISalesCreditNote,
  ISupplierNote,
  ISupplierReturnNote,
  Maybe,
} from '../../../graphql/schema/p4types';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { VariableSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import AutoSizer from 'react-virtualized-auto-sizer';
import { EntityType } from '../../../@types/entityTypes';
import { PrimaryButton } from '../Button';
import { SearchBox } from '@fluentui/react';
import { QueryResult } from '@apollo/client/react/types/types';
import { cloneDeep } from '@apollo/client/utilities';
import { OperationVariables } from '@apollo/client/core';
import CustomerNotesWorkspaceQuery from '../../../graphql/queries/customer/CustomerNotesWorkspaceQuery.graphql';
import SalesNotesWorkspaceQuery from '../../../graphql/queries/sales/SalesNotesWorkspaceQuery.graphql';
import SalesCreditNotesWorkspaceQuery from '../../../graphql/queries/salesLedger/SalesCreditNotesWorkspaceQuery.graphql';
import SupplierNotesWorkspaceQuery from '../../../graphql/queries/supplier/SupplierNotesWorkspaceQuery.graphql';
import PurchaseNotesWorkspaceQuery from '../../../graphql/queries/purchases/PurchaseNotesWorkspaceQuery.graphql';
import SupplierReturnNotesWorkspaceQuery from '../../../graphql/queries/purchases/SupplierReturnNotesWorkspaceQuery.graphql';
import NominalNotesWorkspaceQuery from '../../../graphql/queries/nominal/NominalNotesWorkspaceQuery.graphql';
import DiaryNotesWorkspaceQuery from '../../../graphql/queries/diary/DiaryNotesWorkspaceQuery.graphql';
import ProductNotesWorkspaceQuery from '../../../graphql/queries/product/ProductNotesWorkspaceQuery.graphql';
import AssemblyNotesWorkspaceQuery from '../../../graphql/queries/product/AssemblyNotesWorkspaceQuery.graphql';
import generateClientMutationId from '../../../graphql/schema/generateClientMutationId';
import ConnectedFormContext from '../../Context/ConnectedFormContext';
import { EditableDataGridEmptyComponent } from '../../EditableDataGrid/EditableDataGrid';
import FilterTable from '../../view/FilterTable';
import { UIFilterType } from '../../../utils/FilterHelper';
import { MemoEdit } from './MemoEdit';
import { MemoDisplay } from './MemoDisplay';
import _debounce from 'lodash/debounce';

const FilterDateAny = FilterDate as any;

const PAGE_SIZE = 5;

export enum IMemoEditorType {
  Customer = 'CUSTOMER',
  Supplier = 'SUPPLIER',
  SalesOrder = 'SALES_ORDER',
  SalesCredit = 'SALES_CREDIT',
  PurchaseOrder = 'PURCHASE_ORDER',
  SupplierReturn = 'SUPPLIER_RETURN',
  Nominal = 'NOMINAL',
  Diary = 'DIARY',
  Product = 'PRODUCT',
  Assembly = 'ASSEMBLY',
}

export const MemoContext = React.createContext(
  {} as { resetList?: () => void; type?: IMemoEditorType; noteTypeEntity?: EntityType }
);

export type IP4Note =
  | ICustomerNote
  | ISupplierNote
  | ISalesOrderNote
  | ISalesCreditNote
  | IPurchaseOrderNote
  | ISupplierReturnNote
  | INominalNote
  | IDiaryNote
  | IProductNote
  | IAssemblyNote;

type INoteConfig = {
  mutationNode: DocumentNode; //Mutation graphql node
  refetchNodes: Array<DocumentNode>; //Any queries that need to be refetched after notes are altered, i.e. workspace queries that drive the list.
  mutationKey: string; //The input key field for the note.
  linkKey: string; //The link field on the note (I.e. 'customer' on a CustomerNote) that matches the note to a specific record type.
};

export const useNoteConfig = (type?: IMemoEditorType): INoteConfig => {
  switch (type) {
    case IMemoEditorType.Customer:
      return {
        mutationNode: CustomerNoteMutation,
        refetchNodes: [CustomerNotesWorkspaceQuery],
        mutationKey: 'customerNote',
        linkKey: 'customer',
      };
    case IMemoEditorType.Supplier:
      return {
        mutationNode: SupplierNoteMutation,
        refetchNodes: [SupplierNotesWorkspaceQuery],
        mutationKey: 'supplierNote',
        linkKey: 'supplier',
      };
    case IMemoEditorType.SalesOrder:
      return {
        mutationNode: SalesNoteMutation,
        refetchNodes: [CustomerNotesWorkspaceQuery, SalesNotesWorkspaceQuery],
        mutationKey: 'salesOrderNote',
        linkKey: 'salesOrder',
      };
    case IMemoEditorType.SalesCredit:
      return {
        mutationNode: SalesCreditNoteMutation,
        refetchNodes: [SalesCreditNotesWorkspaceQuery],
        mutationKey: 'salesCreditNote',
        linkKey: 'salesCredit',
      };
    case IMemoEditorType.PurchaseOrder:
      return {
        mutationNode: PurchaseNoteMutation,
        refetchNodes: [SupplierNotesWorkspaceQuery, PurchaseNotesWorkspaceQuery],
        mutationKey: 'purchaseOrderNote',
        linkKey: 'purchaseOrder',
      };
    case IMemoEditorType.SupplierReturn:
      return {
        mutationNode: SupplierReturnNoteMutation,
        refetchNodes: [SupplierReturnNotesWorkspaceQuery],
        mutationKey: 'supplierReturnNote',
        linkKey: 'supplierReturn',
      };
    case IMemoEditorType.Nominal:
      return {
        mutationNode: NominalNoteMutation,
        refetchNodes: [NominalNotesWorkspaceQuery],
        mutationKey: 'nominalNote',
        linkKey: 'nominal',
      };
    case IMemoEditorType.Diary:
      return {
        mutationNode: DiaryNoteMutation,
        refetchNodes: [DiaryNotesWorkspaceQuery],
        mutationKey: 'diaryNote',
        linkKey: 'event',
      };
    case IMemoEditorType.Product:
      return {
        mutationNode: ProductNoteMutation,
        refetchNodes: [ProductNotesWorkspaceQuery],
        mutationKey: 'productNote',
        linkKey: 'product',
      };
    case IMemoEditorType.Assembly:
      return {
        mutationNode: AssemblyNoteMutation,
        refetchNodes: [AssemblyNotesWorkspaceQuery],
        mutationKey: 'assemblyNote',
        linkKey: 'assembly',
      };
  }
  throw new Error(`Mutation could not be found for note type ${type}`);
};

type IContainsFilter = { fieldName: string; containsText: string; filterType: 'CONTAINS' };
type IDateFilter = { fieldName: string; from: string; to: string }; //Format YYYY-MM-DD
type IObjectFilter = { fieldName: string; objects: Array<any> };

type IFilterState = { [fieldName: string]: IContainsFilter | IDateFilter | IObjectFilter };

export const useMemoEditorProps = <TData, TVariables extends OperationVariables = ICustomerNotesQueryVariables>(
  query: DocumentNode,
  type: IMemoEditorType,
  options: { predefinedFilters?: Array<IFilterInput>; skip?: boolean }
) => {
  const [searchValue, setSearchValue] = useState('');
  const connectedForm = useContext(ConnectedFormContext);
  const filterStateConfig = useState<IFilterState>({});

  const filters = useMemo(() => {
    const newFilters: Array<IFilterInput> = options.predefinedFilters || [];
    const [filterState] = filterStateConfig;

    Object.values(filterState).forEach((value) => {
      if ((value as IContainsFilter).containsText) {
        const containsFilter = value as IContainsFilter;
        newFilters.push({
          filterOperation: FilterOperation.Contains,
          filterParameters: {
            field: containsFilter.fieldName,
            value: containsFilter.containsText,
          },
        });
      } else if ((value as IDateFilter).from || (value as IDateFilter).to) {
        const dateFilter = value as IDateFilter;
        if (dateFilter.from) {
          newFilters.push({
            filterOperation: FilterOperation.GreaterThanEqual,
            filterParameters: {
              field: value.fieldName,
              value: dateFilter.from,
            },
          });
        }
        if (dateFilter.to) {
          newFilters.push({
            filterOperation: FilterOperation.LessThanEqual,
            filterParameters: {
              field: value.fieldName,
              value: dateFilter.to,
            },
          });
        }
      } else if ((value as IObjectFilter).objects) {
        const objectsFilter = value as IObjectFilter;
        objectsFilter.objects.forEach((entityRecord) => {
          newFilters.push({
            filterOperation: FilterOperation.In,
            filterParameters: {
              field: `${value.fieldName}.id`,
              value: entityRecord.id,
            },
          });
        });
      }
    });

    if (searchValue.length > 0) {
      newFilters.push({
        filterOperation: FilterOperation.Contains,
        filterParameters: {
          field: 'generic',
          value: searchValue,
        },
      });
    }

    return newFilters;
  }, [filterStateConfig, options.predefinedFilters, searchValue]);

  const variables: ICustomerNotesQueryVariables = {
    first: 20,
    filters: filters,
    orderBy: 'createdAt',
    orderDirection: Direction.Desc,
  };

  const queryProps = useQuery<TData, TVariables>(query, {
    variables: variables as unknown as TVariables,
    skip: options.skip ?? !connectedForm.values?.id,
    fetchPolicy: 'network-only',
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setSearchValueDebounced = useCallback(_debounce(setSearchValue, 300), [setSearchValue]);

  return { queryProps, filterStateConfig, type, setSearchValue: setSearchValueDebounced };
};

const MemoEditor = ({
  noteTypeEntity,
  queryProps,
  filterStateConfig,
  type,
  setSearchValue,
  path,
}: {
  noteTypeEntity?: EntityType;
  queryProps: QueryResult<any, any>;
  filterStateConfig: [IFilterState, Dispatch<SetStateAction<IFilterState>>];
  type: IMemoEditorType;
  setSearchValue: Dispatch<SetStateAction<string>>;
  path?: string;
}) => {
  // refresh notes when the tab gets focus
  useEffect(() => {
    queryProps.refetch();
  }, []);

  const infiniteLoaderRef = useRef<any>(null);
  const [filterState, setFilterState] = filterStateConfig;
  const [newNoteUuid, setNewNoteUuid] = useState<string | undefined>(undefined);

  const onFilterUpdated = useCallback(
    (filter) => {
      setFilterState((oldFilterState) => {
        const newFilterState = cloneDeep(oldFilterState);
        const newFilter: any = { fieldName: filter.fieldName };

        if (filter.type === UIFilterType.filterObjects) {
          newFilter.objects = filter.objects;
        } else if (filter.type === UIFilterType.filterStrings) {
          newFilter.containsText = filter.containsText ? filter.containsText.toLowerCase() : '';
          newFilter.filterType = filter.filterType;
        } else if (filter.type === UIFilterType.filterDates) {
          newFilter.from = filter.from;
          newFilter.to = filter.to;
        }

        newFilterState[filter.fieldName] = newFilter;

        return newFilterState;
      });
    },
    [setFilterState]
  );

  const dismissFilter = useCallback(
    (filter) => {
      setFilterState((oldFilterState) => {
        if (oldFilterState[filter.fieldName]) {
          const newFilterState = cloneDeep(oldFilterState);
          delete newFilterState[filter.fieldName];
          return newFilterState;
        }
        return oldFilterState;
      });
    },
    [setFilterState]
  );

  const filterElements = useMemo(() => {
    const noteObjects = ((filterState.noteType || {}) as IObjectFilter).objects;

    return (
      <>
        <FilterString
          label={'Created By'}
          fieldName={'createdBy'}
          position={0}
          currentViewName={''}
          onFilterUpdated={onFilterUpdated}
          dismissFilter={dismissFilter}
          initiallyOpen={false}
          showType={false}
          type={UIFilterType.filterStrings}
          data-testid={'memo-filters-author'}
          hideRemoveButton={true}
        />
        <FilterDateAny
          label={'Date'}
          fieldName={'createdAt'}
          position={1}
          currentViewName={''}
          onFilterUpdated={onFilterUpdated}
          dismissFilter={dismissFilter}
          initiallyOpen={false}
          showType={false}
          type={UIFilterType.filterDates}
          data-testid={'memo-filters-date'}
          hideRemoveButton={true}
        />
        {noteTypeEntity ? (
          <FilterTable
            {...(noteTypeEntity.dropdown as any)}
            fieldName={'noteType'}
            label={'Note Type'}
            position={2}
            onFilterUpdated={onFilterUpdated}
            dismissFilter={dismissFilter}
            objects={noteObjects}
            type={UIFilterType.filterObjects}
            data-testid={'memo-filters-note-type'}
            styles={{ buttonText: { minWidth: '100px', maxWidth: '250px' } }}
            hideRemoveButton={true}
          />
        ) : null}
      </>
    );
  }, [dismissFilter, filterState.noteType, noteTypeEntity, onFilterUpdated]);

  const { data: notesData, fetchMore } = queryProps;

  const notes: Array<Maybe<ICustomerNote>> = useMemo(() => {
    return (
      notesData?.listData?.edges?.filter((edge?: { node: any }) => !!edge).map((edge: { node: any }) => edge.node) ?? []
    );
  }, [notesData?.listData?.edges]);

  const loadedPagesRef = useRef<{ [key: number]: boolean }>({ 0: true });

  const isItemLoaded = useCallback((index: number) => {
    return loadedPagesRef.current[(index / PAGE_SIZE) >> 0];
  }, []);

  const loadMoreItems: (startIndex: number, stopIndex: number) => Promise<void> | void = (startIndex, stopIndex) => {
    const startPage = (startIndex / PAGE_SIZE) >> 0;
    const stopPage = (stopIndex / PAGE_SIZE) >> 0;
    const promises: Array<Promise<ApolloQueryResult<any>>> = [];
    for (let i = startPage; i <= stopPage; i++) {
      if (!loadedPagesRef.current[i]) {
        promises.push(
          fetchMore({
            variables: {
              first: PAGE_SIZE,
              after: i * PAGE_SIZE,
            },
          })
        );
        loadedPagesRef.current[i] = true;
      }
    }
    return Promise.all(promises) as unknown as Promise<void>;
  };

  const itemCount = notesData?.listData?.count ?? 0;

  const resetList = () => {
    if (infiniteLoaderRef.current) {
      infiniteLoaderRef.current.resetloadMoreItemsCache();
    }
    loadedPagesRef.current = { 0: true };
  };

  const listRef = useRef<any>({});
  const rowHeights = useRef<{ [rowIndex: number]: number }>({});
  const getRowHeight = useCallback(
    (index: number) => {
      if (rowHeights.current[index]) {
        //We have an exact height from the rendered cell, use that.
        return rowHeights.current[index];
      }
      const text = notes[index]?.text;
      if (text) {
        //Otherwise, if we've fetched the value but not it's not been rendered, make an educated guess.  Clamp to min of
        //50 in the case where we don't have any new lines
        return Math.max(50, 10 * (text.match(/\n/g) ?? '').length + 1);
      }
      //Failing those, fall back to a standard value.
      return 50;
    },
    [notes]
  );

  const setRowHeight = useCallback((index: number, size: number) => {
    listRef.current.resetAfterIndex(0);
    rowHeights.current = { ...rowHeights.current, [index]: size };
  }, []);

  const RowMemoDisplay = useCallback(
    (props) => {
      return <MemoDisplay {...props} setRowHeight={setRowHeight} path={path} />;
    },
    [setRowHeight, path]
  );

  return (
    <FluentProvider theme={webLightTheme}>
      <MemoContext.Provider value={{ resetList, type, noteTypeEntity }}>
        <MemoEdit key={newNoteUuid} isOpen={!!newNoteUuid} onClose={() => setNewNoteUuid(undefined)} path={path} />
        <div style={{ display: 'flex', flexDirection: 'row', borderBottom: '1px solid #D2D2D2' }}>
          <SearchBox
            onChange={(ev, newValue) => {
              setSearchValue(newValue ?? '');
            }}
            underlined={true}
            placeholder={'Search'}
            styles={{ root: { marginTop: '9px' } }}
          />
          {filterElements}
          <PrimaryButton onClick={() => setNewNoteUuid(generateClientMutationId())} data-testid={'memo-add-note'}>
            Add Note
          </PrimaryButton>
        </div>
        {itemCount === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <EditableDataGridEmptyComponent />
          </div>
        ) : (
          <AutoSizer disableHeight>
            {({ width }: { width: number }) => (
              <InfiniteLoader
                ref={infiniteLoaderRef}
                isItemLoaded={isItemLoaded}
                itemCount={itemCount}
                loadMoreItems={loadMoreItems}
              >
                {({ onItemsRendered, ref }) => (
                  <VariableSizeList
                    height={window.innerHeight - 250}
                    width={width}
                    itemSize={getRowHeight}
                    itemCount={itemCount}
                    itemData={notes}
                    onItemsRendered={onItemsRendered}
                    ref={(el) => {
                      ref(el);
                      listRef.current = el;
                    }}
                  >
                    {RowMemoDisplay}
                  </VariableSizeList>
                )}
              </InfiniteLoader>
            )}
          </AutoSizer>
        )}
      </MemoContext.Provider>
    </FluentProvider>
  );
};

export default MemoEditor;
